import { QuarterDate, quarterToDate } from '@/utils/quarter';
import { User } from '@prisma/client';
import { isAdminOfProvider } from './auth';
import {
	AdminProvider,
	CapaFund,
	FullFund,
	FundBracket,
	FundBracketTime,
	fundBracketTimes,
	fundExclusionSectors,
	FundExclusionType,
	FundWithProvider,
	Provider,
	ProviderDataType,
	SeriesData,
} from './types';

export const CAPA_FUND_ID = 'CAPA' as const;
export function isCapaFund(fund: Pick<FullFund, 'id'>): fund is CapaFund {
	return fund.id === CAPA_FUND_ID;
}

export function getValueAtQuarter(fundValueData: SeriesData, quarter: QuarterDate): number | undefined {
	return fundValueData.find(point => new Date(point.time).getTime() === quarterToDate(quarter).getTime())?.value;
}

export function filterProviderFunds<
	TP extends Provider,
>(provider: TP, selectedFundIds: Set<string>): (TP['funds'][number] & { provider: TP })[] {
	return provider.funds
		.filter((fund) => selectedFundIds.has(fund.id))
		.map(fund => ({
			...fund,
			provider,
		}));
}

export function filterProvidersFunds<T extends Provider>(
	providers: T[],
	selectedFundIds: Set<string>,
): FundWithProvider<T>[] {
	return providers.flatMap(provider => filterProviderFunds(provider, selectedFundIds));
}

export function getFundWithProviderName(fund: FundWithProvider | CapaFund) {
	if (isCapaFund(fund)) {
		return fund.name;
	}
	return `${fund.provider.name} - ${fund.name}`;
}

export function fundBracketHasAssetAllocationData(fundBracket: FundBracket) {
	return Object.values(fundBracket.assetAllocationData).some(value => value != null);
}

export function fundBracketHasSharpeRatioData(fundBracket: FundBracket) {
	return Object.values(fundBracket.sharpeRatio).some(value => value !== null);
}

export function fundBracketHasValueData(fundBracket: FundBracket): boolean {
	return fundBracket.fundValueData.some(point => point.value != null);
}

export function fundHasInvestsInData(fund: FullFund): boolean {
	return fundExclusionSectors.some(sector => fund.fundExcludes[sector] !== null);
}

export function getDataTypeForUser(user: User | undefined, provider?: AdminProvider | 'CAPA'): ProviderDataType {
	if (user === undefined) {
		return 'free';
	}

	if (user.isAdmin) {
		return 'admin';
	}

	if (provider !== undefined && provider !== 'CAPA' && isAdminOfProvider(provider, user)) {
		return 'admin';
	}

	switch (user.subscriptionType) {
		case 'FREE':
			return 'free';
		case 'PRO':
		case 'PRO_PLUS':
		case 'PROVIDER':
			return 'full';
	}
}

export function getFundBracketTimesForDataType(dataType: ProviderDataType): readonly FundBracketTime[] {
	if (dataType === 'free') {
		return ['30 Years', '5 Years'];
	}

	return fundBracketTimes;
}

export function getFundBracketTimeText(fundBracketTime: FundBracketTime) {
	switch (fundBracketTime) {
		case '30 Years':
			return 'Younger saver';
		case '5 Years':
			return 'Pre retirement';
		case '1 Day':
			return 'At retirement';
		default:
	}
}

export function getShrunkFundBracketTimeText(fundBracketTime: FundBracketTime) {
	switch (fundBracketTime) {
		case '30 Years':
			return 'younger saver';
		case '5 Years':
			return 'pre retirement';
		case '1 Day':
			return 'at retirement';
	}
}

export const fundExcludesText: Record<FundExclusionType, string> = {
	YES: 'Yes',
	NO: 'No',
	PARTIAL: 'Partial',
};

export function getCapaFunds<T extends Provider>(providers: T[]): FundWithProvider<T>[] {
	return providers
		.flatMap(provider =>
			provider.funds
				.filter(fund => fund.isCapaConstituent)
				.map(fund => ({
					...fund,
					provider,
				}))
		).sort(providerFundComparitor);
}

function providerFundComparitor(
	left: FundWithProvider,
	right: FundWithProvider,
) {
	const leftName = `${left.provider.name} ${left.name}`;
	const rightName = `${right.provider.name} ${right.name}`;

	if (leftName > rightName) {
		return 1;
	}
	if (leftName < rightName) {
		return -1;
	}
	return 0;
}

export function getProviderFunds(providers: Provider[]): FundWithProvider[] {
	return providers.flatMap(provider => (
		provider.funds.map(fund => ({
			...fund,
			provider,
		}))
	)).sort(providerFundComparitor);
}

export function getProviderLink(providerSlug: string): string {
	return `/providers/${providerSlug}`;
}

export function getFundLink(providerSlug: string, fundId: string): string {
	return `${getProviderLink(providerSlug)}?fundId=${fundId}}`;
}
